import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, ArrowLeft, ArrowRight, Camera, ShieldCheck } from "lucide-react";

const STEPS = ["Age gate", "Profile", "Interests", "Preferences", "Permissions"];

export default function Onboarding() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [step, setStep] = useState(0);
  const [interests, setInterests] = useState([]);
  const [countries, setCountries] = useState([]);
  const [countryQuery, setCountryQuery] = useState("");
  const [prefQuery, setPrefQuery] = useState("");
  const filteredCountries = countries.filter((c) => c.name.toLowerCase().includes(countryQuery.toLowerCase()));
  const filteredPref = countries.filter((c) => c.name.toLowerCase().includes(prefQuery.toLowerCase()));
  const [form, setForm] = useState({
    ageOk: false,
    display_name: "",
    age: 18,
    country: "",
    country_flag: "",
    languages: ["English"],
    interests: [],
    gender: "",
    gender_preference: "any",
    country_preference: "worldwide",
    permissions: false,
    guidelines: false,
  });

  useEffect(() => {
    api.get("/interests").then((r) => setInterests(r.data.interests));
    api.get("/countries").then((r) => setCountries(r.data.countries));
  }, []);

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const toggleInterest = (i) => {
    setForm((f) => ({ ...f, interests: f.interests.includes(i) ? f.interests.filter((x) => x !== i) : [...f.interests, i] }));
  };

  const finish = async () => {
    if (form.interests.length < 3) return toast.error("Pick at least 3 interests");
    if (!form.country) return toast.error("Choose your country");
    if (!form.guidelines) return toast.error("You must accept the guidelines");
    try {
      const { data } = await api.post("/onboarding", {
        display_name: form.display_name || undefined,
        age: Number(form.age),
        country: form.country,
        country_flag: form.country_flag,
        languages: form.languages,
        interests: form.interests,
        gender: form.gender || undefined,
        gender_preference: form.gender_preference,
        country_preference: form.country_preference,
        agreed_guidelines: true,
      });
      setUser(data);
      toast.success("You're all set. Finding your first match…");
      nav("/match");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const canProceed = () => {
    if (step === 0) return form.ageOk;
    if (step === 1) return form.country;
    if (step === 2) return form.interests.length >= 3;
    if (step === 3) return !!form.gender;
    return form.guidelines;
  };

  return (
    <div className="min-h-screen orbit-bg-grid flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-2xl orbit-glass rounded-3xl p-8">
        {/* progress */}
        <div className="flex items-center gap-2 mb-8">
          {STEPS.map((s, i) => (
            <div key={`step-${i}`} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-cyan-400" : "bg-white/10"}`} />
          ))}
        </div>
        <div className="text-xs font-mono uppercase text-cyan-300 mb-2">Step {step + 1} / {STEPS.length}</div>
        <h2 className="font-display text-3xl font-bold mb-6">{STEPS[step]}</h2>

        {step === 0 && (
          <div className="space-y-4">
            <p className="text-slate-300">Orbit Hello is for adults only. You must confirm you are at least 18 years old to continue.</p>
            <label className="flex items-start gap-3 orbit-chip cursor-pointer !py-3 !px-4" data-testid="onboarding-age-checkbox">
              <input type="checkbox" checked={form.ageOk} onChange={(e) => setForm({ ...form, ageOk: e.target.checked })} className="mt-1" />
              <span className="text-sm text-slate-200">I confirm I am 18 or older and I understand Orbit Hello enforces zero tolerance for exploitation of minors.</span>
            </label>
            <div>
              <label className="text-xs font-mono uppercase text-slate-400">Your age</label>
              <input type="number" min={18} max={120} value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })}
                className="w-full mt-1 px-4 py-3 rounded-xl bg-slate-950/60 border border-white/10 outline-none focus:border-cyan-400 text-slate-100" data-testid="onboarding-age-input" />
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-mono uppercase text-slate-400">Display name (optional)</label>
              <input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} maxLength={40}
                className="w-full mt-1 px-4 py-3 rounded-xl bg-slate-950/60 border border-white/10 outline-none focus:border-cyan-400 text-slate-100" data-testid="onboarding-name-input" />
            </div>
            <div>
              <label className="text-xs font-mono uppercase text-slate-400">Country</label>
              <input value={countryQuery} onChange={(e) => setCountryQuery(e.target.value)} placeholder="Search 200+ countries…"
                className="w-full mt-1 px-4 py-2.5 rounded-xl bg-slate-950/60 border border-white/10 outline-none focus:border-cyan-400 text-slate-100 text-sm" data-testid="onboarding-country-search" />
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-56 overflow-y-auto pr-1" data-testid="onboarding-country-select">
                {filteredCountries.map((c) => (
                  <button key={c.code} type="button"
                    onClick={() => setForm({ ...form, country: c.name, country_flag: c.flag })}
                    className={`orbit-chip !justify-start ${form.country === c.name ? "active" : ""}`}
                    data-testid={`country-${c.code.toLowerCase()}`}>
                    <span className="mr-1">{c.flag}</span>{c.name}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-mono uppercase text-slate-400">Languages you speak</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {["English", "Spanish", "Portuguese", "French", "German", "Hindi", "Arabic", "Japanese", "Korean", "Italian"].map((l) => (
                  <button key={l} type="button" onClick={() => setForm((f) => ({ ...f, languages: f.languages.includes(l) ? f.languages.filter((x) => x !== l) : [...f.languages, l] }))}
                    className={`orbit-chip ${form.languages.includes(l) ? "active" : ""}`} data-testid={`onboarding-lang-${l.toLowerCase()}`}>{l}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <p className="text-slate-400 text-sm mb-4">Pick at least <span className="text-cyan-300">3 interests</span>. We'll surface people who share them.</p>
            <div className="flex flex-wrap gap-2" data-testid="onboarding-interests-grid">
              {interests.map((i) => (
                <button key={i} type="button" onClick={() => toggleInterest(i)}
                  className={`orbit-chip ${form.interests.includes(i) ? "active" : ""}`} data-testid={`interest-${i.toLowerCase()}`}>
                  {form.interests.includes(i) && <Check className="w-3 h-3" />} {i}
                </button>
              ))}
            </div>
            <div className="mt-4 text-xs font-mono text-slate-500">{form.interests.length} selected</div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-mono uppercase text-slate-400">I am</label>
              <div className="flex flex-wrap gap-2 mt-2" data-testid="onboarding-gender">
                {[["male", "Man"], ["female", "Woman"], ["nonbinary", "Non-binary"]].map(([g, label]) => (
                  <button key={g} type="button" onClick={() => setForm({ ...form, gender: g })}
                    className={`orbit-chip ${form.gender === g ? "active" : ""}`} data-testid={`gender-${g}`}>{label}</button>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5">Only used for matching — never shown publicly.</p>
            </div>
            <div>
              <label className="text-xs font-mono uppercase text-slate-400">Match with</label>
              <div className="flex flex-wrap gap-2 mt-2">
                <button type="button" onClick={() => setForm({ ...form, gender_preference: "any" })}
                  className={`orbit-chip ${form.gender_preference === "any" ? "active" : ""}`} data-testid="gender-pref-any">Everyone · Free</button>
                {[["male", "Men only"], ["female", "Women only"], ["nonbinary", "Non-binary only"]].map(([g, label]) => (
                  <button key={g} type="button" onClick={() => toast.info("Matching only men or only women is a VIP feature — mixed matching is always free. Unlock it anytime from your dashboard.")}
                    className="orbit-chip opacity-60" data-testid={`gender-pref-${g}`}>{label} <span className="ml-1 text-[10px] font-mono text-fuchsia-300">VIP</span></button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-mono uppercase text-slate-400">Country preference</label>
              <input value={prefQuery} onChange={(e) => setPrefQuery(e.target.value)} placeholder="Search 200+ countries…"
                className="w-full mt-1 px-4 py-2.5 rounded-xl bg-slate-950/60 border border-white/10 outline-none focus:border-cyan-400 text-slate-100 text-sm" data-testid="onboarding-country-pref-search" />
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-56 overflow-y-auto pr-1" data-testid="onboarding-country-pref">
                <button type="button" onClick={() => setForm({ ...form, country_preference: "worldwide" })}
                  className={`orbit-chip !justify-start ${form.country_preference === "worldwide" ? "active" : ""}`}
                  data-testid="country-pref-worldwide">🌍 Worldwide</button>
                {filteredPref.map((c) => (
                  <button key={c.code} type="button"
                    onClick={() => toast.info("Country filters unlock with VIP Orbit or Galaxy Pass — you can change this later from your dashboard.")}
                    className="orbit-chip !justify-start opacity-60"
                    data-testid={`country-pref-${c.code.toLowerCase()}`}>
                    <span className="mr-1">{c.flag}</span>{c.name} <span className="ml-auto text-[10px] font-mono text-fuchsia-300">VIP</span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">Country filters are a VIP / Galaxy feature. Free members match worldwide.</p>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <div className="orbit-glass rounded-2xl p-5 flex items-start gap-3">
              <Camera className="w-6 h-6 text-cyan-300 mt-0.5" />
              <div>
                <div className="font-semibold">Camera & microphone</div>
                <p className="text-sm text-slate-400 mt-1">We'll simulate your camera in this MVP. When live WebRTC ships you'll grant permission here.</p>
                <button type="button" onClick={() => { setForm({ ...form, permissions: true }); toast.success("Simulated camera ready"); }} className="mt-3 orbit-chip active" data-testid="onboarding-permission-btn">Simulate permission</button>
              </div>
            </div>
            <label className="flex items-start gap-3 orbit-glass rounded-2xl p-5 cursor-pointer" data-testid="onboarding-guidelines-check">
              <input type="checkbox" checked={form.guidelines} onChange={(e) => setForm({ ...form, guidelines: e.target.checked })} className="mt-1" />
              <div>
                <div className="font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-400" /> Community pledge</div>
                <p className="text-sm text-slate-400 mt-1">I will be respectful. No nudity, harassment, hate, threats, spam, or illegal activity. Violations result in permanent bans.</p>
              </div>
            </label>
          </div>
        )}

        <div className="mt-8 flex items-center justify-between">
          <button onClick={back} disabled={step === 0} className="text-slate-400 hover:text-slate-100 disabled:opacity-30 inline-flex items-center gap-1 text-sm" data-testid="onboarding-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={next} disabled={!canProceed()} className="orbit-btn-primary inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="onboarding-next-btn">
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={finish} disabled={!canProceed()} className="orbit-btn-primary inline-flex items-center gap-2 disabled:opacity-40" data-testid="onboarding-finish-btn">
              Enter Orbit Hello <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
